import {
  type CampaignDetailCampaign,
  type CampaignSettingsUpdate,
  campaignSettingsUpdateSchema,
} from "@adpilot/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconEdit, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { getApiErrorMessage } from "@/lib/api-error";
import { updateCampaignSettings } from "@/lib/campaign-settings-api";
import { fetchPortfolios } from "@/lib/portfolio-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

interface CampaignEditDrawerProps {
  campaign: CampaignDetailCampaign;
  canWrite: boolean;
}

export function CampaignEditDrawer({ campaign, canWrite }: CampaignEditDrawerProps) {
  const { t } = useTranslation("campaign");
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const disabledReason = !canWrite
    ? t("list.stateMenu.permission")
    : campaign.campaignType !== "sp"
      ? t("list.stateMenu.spOnly")
      : campaign.status === "archived"
        ? t("detail.edit.archived")
        : undefined;
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!pending) setOpen(next);
      }}
    >
      {disabledReason ? (
        <UnavailableControl variant="outline" reason={disabledReason}>
          <IconEdit data-icon="inline-start" />
          {t("detail.edit.title")}
        </UnavailableControl>
      ) : (
        <SheetTrigger render={<Button variant="outline" size="sm" />}>
          <IconEdit data-icon="inline-start" />
          {t("detail.edit.title")}
        </SheetTrigger>
      )}
      <SheetContent
        className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-[500px]"
        showCloseButton={false}
      >
        <SheetHeader className="shrink-0 border-b border-line px-5 py-3.5">
          <SheetTitle className="text-base leading-6">{t("detail.edit.title")}</SheetTitle>
          <SheetDescription className="sr-only">
            {t("detail.edit.description", { name: campaign.name })}
          </SheetDescription>
          <Button
            variant="ghost"
            size="icon-sm"
            className="absolute right-3 top-2.5"
            aria-label={t("detail.edit.close")}
            disabled={pending}
            onClick={() => setOpen(false)}
          >
            <IconX />
          </Button>
        </SheetHeader>
        {open && (
          <CampaignEditForm
            campaign={campaign}
            onClose={() => setOpen(false)}
            onPendingChange={setPending}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

interface CampaignEditFormProps {
  campaign: CampaignDetailCampaign;
  onClose: () => void;
  onPendingChange: (pending: boolean) => void;
}

function CampaignEditForm({
  campaign: latestCampaign,
  onClose,
  onPendingChange,
}: CampaignEditFormProps) {
  // Keep the edit baseline stable while queries refresh behind the drawer.
  const [campaign] = useState(latestCampaign);
  const { t } = useTranslation("campaign");
  const id = useId();
  const client = useQueryClient();
  // The campaign may belong to another shop than the selected one (ADP-110).
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const shopId = campaign.shopId ?? currentShopId;
  const portfolios = useQuery({
    queryKey: ["portfolios", shopId],
    queryFn: () => fetchPortfolios(shopId),
  });
  const defaultValues = {
    name: campaign.name,
    dailyBudget: campaign.dailyBudget?.toString() ?? "",
    endDate: campaign.endDate ?? "",
    portfolioId: campaign.portfolioId ?? "",
  };
  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t("detail.edit.nameInvalid"))
      .max(128, t("detail.edit.nameInvalid")),
    dailyBudget: z
      .string()
      .refine(
        (value) =>
          value === defaultValues.dailyBudget ||
          (value.trim() !== "" &&
            campaignSettingsUpdateSchema.safeParse({ dailyBudget: Number(value) }).success),
        t("detail.edit.budgetInvalid"),
      ),
    endDate: z
      .string()
      .refine(
        (value) =>
          value === defaultValues.endDate ||
          (value !== "" &&
            z.string().date().safeParse(value).success &&
            (!campaign.startDate || value >= campaign.startDate)),
        t("detail.edit.dateInvalid"),
      ),
    portfolioId: z.string(),
  });
  const form = useForm({ resolver: zodResolver(schema), defaultValues });
  const mutation = useMutation({
    mutationFn: (patch: CampaignSettingsUpdate) => updateCampaignSettings(campaign.id, patch),
    onMutate: () => onPendingChange(true),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["campaign", campaign.id] }),
        client.invalidateQueries({ queryKey: ["campaigns"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["portfolios"] }),
      ]);
      toast.success(t("detail.edit.success"));
      onClose();
    },
    // Shown inline under the form; the global mutation onError owns toasts.
    onError: async (error) => form.setError("root", { message: await getApiErrorMessage(error) }),
    onSettled: () => onPendingChange(false),
  });
  const choices = (portfolios.data ?? [])
    .filter((item) => item.state !== "archived")
    .map(({ id, name }) => ({ id, name }));
  if (campaign.portfolioId && !choices.some((item) => item.id === campaign.portfolioId)) {
    choices.unshift({
      id: campaign.portfolioId,
      name: campaign.portfolioName ?? "—",
    });
  }
  const errors = form.formState.errors;
  const errorText = (field: "name" | "dailyBudget" | "endDate") =>
    errors[field] && (
      <p
        id={`${id}-${field}-error`}
        role="alert"
        className="text-[11px] leading-4 text-destructive-foreground"
      >
        {errors[field]?.message}
      </p>
    );

  const submit = form.handleSubmit((values) => {
    const patch: CampaignSettingsUpdate = {};
    if (values.name !== campaign.name) patch.name = values.name;
    if (values.dailyBudget !== defaultValues.dailyBudget)
      patch.dailyBudget = Number(values.dailyBudget);
    if (values.endDate !== defaultValues.endDate) patch.endDate = values.endDate || null;
    if (values.portfolioId !== defaultValues.portfolioId)
      patch.portfolioId = values.portfolioId || null;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(patch);
  });

  return (
    <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
      <fieldset
        disabled={mutation.isPending}
        className="min-h-0 min-w-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 text-[11px] leading-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-name`}>
            {t("detail.edit.name")} <span className="text-destructive-foreground">*</span>
          </Label>
          <Input
            size="sm"
            id={`${id}-name`}
            {...form.register("name")}
            aria-required
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${id}-name-error` : undefined}
          />
          {errorText("name")}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-portfolio`}>
            {t("detail.edit.portfolio")}
          </Label>
          <Controller
            control={form.control}
            name="portfolioId"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => field.onChange(value ?? "")}
                disabled={portfolios.isPending || portfolios.isError || mutation.isPending}
                items={[
                  { value: "", label: t("detail.edit.noPortfolio") },
                  ...choices.map((item) => ({ value: item.id, label: item.name })),
                ]}
              >
                <SelectTrigger
                  size="sm"
                  id={`${id}-portfolio`}
                  className="w-full"
                  onBlur={field.onBlur}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="" disabled={!!campaign.portfolioId}>
                    {t("detail.edit.noPortfolio")}
                  </SelectItem>
                  {choices.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {portfolios.isPending && (
            <p role="status" className="text-[11px] leading-4 text-muted-foreground">
              {t("detail.edit.loadingPortfolios")}
            </p>
          )}
          {portfolios.isError && (
            <div
              role="alert"
              className="flex items-center gap-2 text-[11px] leading-4 text-destructive-foreground"
            >
              {t("detail.edit.portfoliosFailed")}
              <Button
                type="button"
                variant="link"
                size="xs"
                onClick={() => void portfolios.refetch()}
              >
                {t("detail.edit.retry")}
              </Button>
            </div>
          )}
          {campaign.portfolioId && (
            <p className="text-[11px] leading-4 text-muted-foreground">
              {t("detail.edit.noClearPortfolio")}
            </p>
          )}
        </div>
        <UnavailableOptions
          label={t("detail.edit.site")}
          reason={t("detail.edit.siteUnavailable")}
          options={[
            {
              value: "amazon",
              label: t("detail.edit.amazonSites"),
              description: t("detail.edit.amazonSitesHelp"),
            },
            {
              value: "business",
              label: t("detail.edit.amazonBusiness"),
              description: t("detail.edit.amazonBusinessHelp"),
            },
          ]}
        />
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-start`}>
            {t("detail.edit.startDate")} <span className="text-destructive-foreground">*</span>
          </Label>
          <Input
            size="sm"
            id={`${id}-start`}
            type="date"
            value={campaign.startDate ?? ""}
            disabled
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-endDate`}>
            {t("detail.edit.endDate")}
          </Label>
          <Input
            size="sm"
            id={`${id}-endDate`}
            type="date"
            min={campaign.startDate ?? undefined}
            {...form.register("endDate")}
            aria-invalid={!!errors.endDate}
            aria-describedby={`${id}-endDate-help${errors.endDate ? ` ${id}-endDate-error` : ""}`}
          />
          <p id={`${id}-endDate-help`} className="text-[11px] leading-4 text-muted-foreground">
            {campaign.endDate
              ? t("detail.edit.noClearDate")
              : !form.watch("endDate")
                ? t("detail.shell.noEndDate")
                : null}
          </p>
          {errorText("endDate")}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-dailyBudget`}>
            {t("detail.edit.dailyBudget")} ({campaign.budgetHealth.currency ?? "USD"}){" "}
            <span className="text-destructive-foreground">*</span>
          </Label>
          <Input
            size="sm"
            id={`${id}-dailyBudget`}
            type="number"
            step="0.01"
            min="0"
            {...form.register("dailyBudget")}
            // The server rejects daily edits of monthly budgets (unsupported_budget_type).
            disabled={campaign.budgetType === "monthly"}
            aria-required
            aria-invalid={!!errors.dailyBudget}
            aria-describedby={errors.dailyBudget ? `${id}-dailyBudget-error` : undefined}
          />
          {campaign.budgetType === "monthly" && (
            <p className="text-[11px] leading-4 text-muted-foreground">
              {t("detail.edit.monthlyBudget")}
            </p>
          )}
          {errorText("dailyBudget")}
        </div>
        <UnavailableOptions
          framed
          label={t("detail.edit.biddingStrategy")}
          reason={t("detail.edit.biddingUnavailable")}
          value={campaign.biddingStrategy}
          options={[
            ...["LEGACY_FOR_SALES", "AUTO_FOR_SALES", "MANUAL"].map((value) => ({
              value,
              label: t(`list.biddingStrategy.${value}`),
              description: t(`detail.edit.biddingHelp.${value}`),
            })),
            {
              value: "RULE_BASED",
              label: t("detail.edit.ruleBidding"),
              description: t("detail.edit.ruleHelp"),
            },
          ]}
        />
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-audience-toggle`}>
              {t("detail.edit.audience")}
            </Label>
            <Switch
              id={`${id}-audience-toggle`}
              disabled
              checked={false}
              aria-describedby={`${id}-audience-help`}
            />
          </div>
          <p className="text-[11px] leading-4 text-muted-foreground">
            {t("detail.edit.audienceHelp")}
          </p>
          <p id={`${id}-audience-help`} className="text-[11px] leading-4 text-muted-foreground">
            {t("detail.edit.advancedUnavailable")}
          </p>
          <UnavailableOptions
            label={t("detail.edit.audienceType")}
            reason={t("detail.edit.advancedUnavailable")}
            options={[
              { value: "amc", label: t("detail.edit.amcAudience") },
              { value: "amazon", label: t("detail.edit.amazonAudience") },
            ]}
          />
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-audience`}>
            {t("detail.edit.audienceSelect")}
          </Label>
          <Input
            size="sm"
            id={`${id}-audience`}
            disabled
            placeholder={t("detail.edit.notSynced")}
          />
          <Button type="button" variant="link" size="xs" disabled>
            {t("detail.edit.audienceRefresh")}
          </Button>
          <Label className="text-[11px] leading-[14px]" htmlFor={`${id}-percentage`}>
            {t("detail.edit.audiencePercent")} (%)
          </Label>
          <Input
            size="sm"
            id={`${id}-percentage`}
            type="number"
            disabled
            placeholder={t("detail.edit.notSynced")}
          />
        </div>
        <UnavailableOptions
          label={t("detail.edit.offsite")}
          reason={t("detail.edit.advancedUnavailable")}
          options={[
            {
              value: "reach",
              label: t("detail.edit.expandReach"),
              description: t("detail.edit.expandReachHelp"),
            },
            {
              value: "spend",
              label: t("detail.edit.limitSpend"),
              description: t("detail.edit.limitSpendHelp"),
            },
          ]}
        />
      </fieldset>
      {errors.root && (
        <p
          role="alert"
          className="border-t border-line px-5 py-2.5 text-[11px] leading-4 text-destructive-foreground"
        >
          {errors.root.message}
        </p>
      )}
      <SheetFooter className="shrink-0 flex-row justify-end border-t border-line px-5 py-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={mutation.isPending}
          onClick={onClose}
        >
          {t("detail.edit.cancel")}
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending || !form.formState.isDirty}>
          {mutation.isPending && <Spinner data-icon="inline-start" />}
          {t("detail.edit.confirm")}
        </Button>
      </SheetFooter>
    </form>
  );
}

function UnavailableOptions({
  label,
  reason,
  value,
  options,
  framed = false,
}: {
  label: string;
  reason: string;
  framed?: boolean;
  value?: string | null;
  options: { value: string; label: string; description?: string }[];
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <p id={id} className="text-[11px] font-medium leading-[14px]">
        {label}
      </p>
      <RadioGroup
        disabled
        value={value ?? null}
        aria-labelledby={id}
        aria-describedby={`${id}-reason`}
      >
        {options.map((option) => (
          <Label
            key={option.value}
            className={cn(
              "items-start gap-2.5 text-[11px] font-normal leading-4",
              framed && "rounded-[8px] border border-line px-3 py-2.5",
              framed && value === option.value && "border-primary/40 bg-primary/5",
            )}
          >
            <Radio value={option.value} className="mt-0.5 data-checked:data-disabled:bg-primary" />
            <span>
              {option.label}
              {option.description && (
                <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
                  {option.description}
                </span>
              )}
            </span>
          </Label>
        ))}
      </RadioGroup>
      <p id={`${id}-reason`} className="text-[11px] leading-4 text-muted-foreground">
        {reason}
      </p>
    </div>
  );
}
